import React from 'react';
import { DynamicFormWidgetInputType, FormFieldType } from './types/inputTypes';
import {
  InitialMultiAutocompleteSearch,
  OnAutocompleteSearch,
  OnFieldBlur,
  OnFieldChange,
} from './types';
import { BooleanRecord, IAbstractRecord, IPagination } from '@models';
import TextInputFormField from '../form-fields/TextInputFormField';
import SelectFormField from '../form-fields/SelectFormField';
import TextAreaFormField from '../form-fields/TextAreaFormField';
import SplitPrefixFormField from '../form-fields/SplitPrefixFormField';
import CurrencyFormField from '../form-fields/CurrencyFormField';
import NumberInputFormField from '../form-fields/NumberInputFormField';
import PercentageFormField from '../form-fields/PercentageFormField';
import MultiselectFormField from '../form-fields/MultiselectFormField';
import DatePickerFormField from '../form-fields/DatePickerFormField';
import DateRangeFormField from '../form-fields/DateRangeFormField';
import AutocompleteFormField from '../form-fields/AutocompleteFormField';
import MultiAutocompleteFormField from '../form-fields/MultiAutocompleteFormField';
import RepeaterBoxFormField from '../form-fields/repeater-box/RepeaterBoxFormField';
import EnhancedInternationalPhoneInput from '../_old/form/EnhancedInternationalPhoneInput';
import PasswordFormField from '../form-fields/PasswordFormField';
import SwitchFormField from '../form-fields/SwitchFormField';
import { CDN_URL } from '@utils';
import CheckboxFormField from '../form-fields/CheckboxFormField';
import UploadFileFormField from '@components/form-fields/UploadFileFormField';

type IDynamicFormInputProps = DynamicFormWidgetInputType & {
  onFieldChange: OnFieldChange;
  onFieldBlur: OnFieldBlur;
  loadingFields: Record<string, boolean | BooleanRecord>;
  sectionIndex: number;
  onAutocompleteSearch: OnAutocompleteSearch;
  initialMultiAutocompleteSearch: InitialMultiAutocompleteSearch;
  values: IAbstractRecord;
  errors: IAbstractRecord;
  touched: IAbstractRecord;
};

const DynamicFormInput: React.FC<IDynamicFormInputProps> = (inputProps) => {
  const {
    // value,
    // error,
    name,
    title,
    type,
    placeholder,
    required,
    disabled,
    material,
    hidden,
    conditionalDisable,
    conditionalHidden,
    conditionalRequired,
    onAutocompleteSearch,
    initialMultiAutocompleteSearch,
    onFieldChange,
    onFieldBlur,
    loadingFields,
    sectionIndex,
    values,
    errors,
    touched,
    ...rest
  } = inputProps;
  const customProps = { ...rest } as IAbstractRecord;

  const onFieldChangeByEvent = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    onFieldChange(event.target.name, event.target.value, sectionIndex);
  };

  const renderInput = () => {
    const isDisabled =
      disabled || (conditionalDisable && conditionalDisable(values));
    const isHidden = hidden || (conditionalHidden && conditionalHidden(values));
    const isRequired =
      required || (conditionalRequired && conditionalRequired(values));
    const inputTitle = isRequired ? `${title}*` : title;

    if (isHidden) {
      return <></>;
    }

    switch (type) {
      case FormFieldType.text:
        return (
          <TextInputFormField
            name={name}
            title={inputTitle}
            placeholder={placeholder}
            value={values[name]}
            disabled={isDisabled}
            onChange={onFieldChangeByEvent}
            error={touched[name] ? errors[name] : ''}
            material={material}
            onBlur={() => {
              onFieldBlur(name);
            }}
          />
        );
      case FormFieldType.textArea:
        return (
          <TextAreaFormField
            name={name}
            title={inputTitle}
            placeholder={placeholder}
            value={values[name]}
            rows={customProps.numRows}
            disabled={isDisabled}
            onChange={onFieldChangeByEvent}
            error={touched[name] ? errors[name] : ''}
            material={material}
            onBlur={() => {
              onFieldBlur(name);
            }}
          />
        );
      case FormFieldType.splitPrefix:
        return (
          <SplitPrefixFormField
            name={name}
            title={inputTitle}
            placeholder={placeholder}
            value={values[name]}
            inputType={customProps.inputType}
            prefixValue={customProps.prefixValue}
            disabled={isDisabled}
            error={touched[name] ? errors[name] : ''}
            material={material}
            onChange={onFieldChangeByEvent}
            onBlur={() => {
              onFieldBlur(name);
            }}
          />
        );
      case FormFieldType.currency:
        return (
          <CurrencyFormField
            name={name}
            title={inputTitle}
            placeholder={placeholder}
            value={values[name]}
            allowNegative={customProps.allowNegative}
            currencySymbol={customProps.currencySymbol}
            disabled={isDisabled}
            error={touched[name] ? errors[name] : ''}
            material={material}
            onChange={onFieldChangeByEvent}
            onBlur={() => {
              onFieldBlur(name);
            }}
          />
        );
      case FormFieldType.number:
        return (
          <NumberInputFormField
            name={name}
            title={inputTitle}
            placeholder={placeholder}
            value={values[name]}
            minValue={customProps.minValue}
            maxValue={customProps.maxValue}
            allowNegativeValues={customProps.allowNegativeValues}
            allowExponential={customProps.allowExponential}
            formatOnBlur={customProps.formatOnBlur}
            maxDecimalPrecision={customProps.maxDecimalPrecision}
            disabled={isDisabled}
            error={touched[name] ? errors[name] : ''}
            material={material}
            onChange={onFieldChangeByEvent}
            onBlur={() => {
              onFieldBlur(name);
            }}
          />
        );
      case FormFieldType.percentage:
        return (
          <PercentageFormField
            name={name}
            title={inputTitle}
            placeholder={placeholder}
            value={values[name]}
            disableNegativeValues={customProps.disableNegativeValues}
            disabled={isDisabled}
            error={touched[name] ? errors[name] : ''}
            material={material}
            onChange={onFieldChangeByEvent}
            onBlur={() => {
              onFieldBlur(name);
            }}
          />
        );
      case FormFieldType.select:
        return customProps.injectGlobalLov ? (
          <SelectFormField
            name={name}
            title={inputTitle}
            placeholder={placeholder}
            value={values[name]}
            injectGlobalLov={customProps.injectGlobalLov}
            canClearValue={customProps.canClearValue}
            freeSolo={customProps.freeSolo}
            loader={(loadingFields[name] as boolean) || false}
            disabled={isDisabled}
            onChange={(v) => onFieldChange(name, v, sectionIndex)}
            error={touched[name] ? errors[name] : ''}
            material={material}
            onBlur={() => {
              onFieldBlur(name);
            }}
          />
        ) : (
          <SelectFormField
            name={name}
            title={inputTitle}
            placeholder={placeholder}
            value={values[name]}
            selectOptions={customProps.selectOptions}
            canClearValue={customProps.canClearValue}
            freeSolo={customProps.freeSolo}
            loader={(loadingFields[name] as boolean) || false}
            disabled={isDisabled}
            onChange={(v) => onFieldChange(name, v, sectionIndex)}
            error={touched[name] ? errors[name] : ''}
            material={material}
            onBlur={() => {
              onFieldBlur(name);
            }}
          />
        );
      case FormFieldType.multiSelect:
        return customProps.injectGlobalLov ? (
          <MultiselectFormField
            name={name}
            title={inputTitle}
            placeholder={placeholder}
            values={values[name]}
            injectGlobalLov={customProps.injectGlobalLov}
            freeSolo={customProps.freeSolo}
            maxTagCount={customProps.maxTagCount}
            loader={(loadingFields[name] as boolean) || false}
            disabled={isDisabled}
            onChange={(v) => onFieldChange(name, v, sectionIndex)}
            error={touched[name] ? errors[name] : ''}
            material={material}
            onBlur={() => {
              onFieldBlur(name);
            }}
          />
        ) : (
          <MultiselectFormField
            name={name}
            title={inputTitle}
            placeholder={placeholder}
            values={values[name]}
            selectOptions={customProps.selectOptions}
            freeSolo={customProps.freeSolo}
            maxTagCount={customProps.maxTagCount}
            loader={(loadingFields[name] as boolean) || false}
            disabled={isDisabled}
            onChange={(v) => onFieldChange(name, v, sectionIndex)}
            error={touched[name] ? errors[name] : ''}
            material={material}
            onBlur={() => {
              onFieldBlur(name);
            }}
          />
        );
      case FormFieldType.date:
        return (
          <DatePickerFormField
            name={name}
            title={inputTitle}
            placeholder={placeholder}
            value={values[name]}
            disabled={isDisabled}
            minDate={customProps.minDate}
            maxDate={customProps.maxDate}
            canClearDate={customProps.canClearDate}
            onDateChange={(v) => onFieldChange(name, v, sectionIndex)}
            error={touched[name] ? errors[name] : ''}
            material={material}
            onBlur={() => {
              onFieldBlur(name);
            }}
          />
        );
      case FormFieldType.dateRange:
        return (
          <DateRangeFormField
            key={name}
            name={name}
            title={title}
            value={values[name]}
            error={touched[name] ? errors[name] : ''}
            onDateRangeChange={(dateValue) => {
              onFieldChange(name, dateValue, sectionIndex);
            }}
            disabled={customProps.inputDisabled}
            material={material}
            minDate={customProps.minDate}
            maxDate={customProps.maxDate}
            placeholder={placeholder}
          />
        );
      case FormFieldType.autocomplete:
        return (
          <AutocompleteFormField
            key={name}
            name={name}
            title={title}
            placeholder={placeholder}
            value={values[name]}
            error={touched[name] ? errors[name] : ''}
            disabled={isDisabled}
            material={material}
            loader={(loadingFields[name] as boolean) || false}
            maxItemsDisplayed={customProps.maxItemsDisplayed}
            initialOptions={customProps.initialOptions}
            canClearValue={customProps.canClearValue}
            onChange={(v) => {
              onFieldChange(name, v, sectionIndex);
            }}
            fetchOptions={function (
              query: string,
              pagination: IPagination,
              hasDefaultValue?: boolean
            ): Promise<{
              options: Record<string, string>;
              totalCount: number;
            }> {
              if (onAutocompleteSearch) {
                return onAutocompleteSearch(
                  name,
                  query,
                  pagination,
                  hasDefaultValue
                );
              }

              return new Promise<{
                options: Record<string, string>;
                totalCount: number;
              }>((resolve) => {
                resolve({ options: {}, totalCount: 0 });
              });
            }}
          />
        );
      case FormFieldType.multiAutocomplete:
        return (
          <MultiAutocompleteFormField
            key={name}
            name={name}
            title={title}
            placeholder={placeholder}
            value={values[name]}
            disabled={disabled || disabled}
            material={material}
            onChange={(v) => {
              onFieldChange(name, v, sectionIndex);
            }}
            onBlur={() => onFieldBlur(name)}
            maxItemsDisplayed={customProps.maxItemsDisplayed}
            canClearValue={customProps.canClearValue}
            fetchOptions={function (
              query: string,
              pagination: IPagination
            ): Promise<{
              options: Record<string, string>;
              totalCount: number;
            }> {
              if (onAutocompleteSearch) {
                return onAutocompleteSearch(name, query, pagination);
              }

              return new Promise<{
                options: Record<string, string>;
                totalCount: number;
              }>((resolve) => {
                resolve({ options: {}, totalCount: 0 });
              });
            }}
            fetchOptionsByValues={function (
              values: string[],
              pagination: IPagination
            ): Promise<{
              options: Record<string, string>;
              totalCount: number;
            }> {
              if (initialMultiAutocompleteSearch) {
                return initialMultiAutocompleteSearch(name, values, pagination);
              }
              return new Promise<{
                options: Record<string, string>;
                totalCount: number;
              }>((resolve) => {
                resolve({ options: {}, totalCount: 0 });
              });
            }}
          />
        );
      case FormFieldType.repeaterBox:
        return (
          <RepeaterBoxFormField
            key={name}
            name={name}
            title={title}
            placeholder={placeholder}
            values={values[name]}
            error={errors[name]}
            inputs={customProps.inputs}
            addButtonText={customProps.addButtonText}
            dynamicAddButtonText={customProps.dynamicAddButtonText}
            loadingFields={loadingFields[name] as BooleanRecord}
            maxRepeaterLength={customProps.maxRepeaterLength}
            minRepeaterLength={customProps.minRepeaterLength}
            onChange={(v, subFieldName) => {
              onFieldChange(name, v, sectionIndex, {
                index: sectionIndex,
                subFieldName,
                input: inputProps,
              });
            }}
            onBlur={(subFieldName: string, index: number) => {
              onFieldBlur(name, { subFieldName, index, input: inputProps });
            }}
            onAutocompleteSearch={onAutocompleteSearch}
            disabled={isDisabled}
            material={material}
            touched={touched[name]}
          />
        );
      case FormFieldType.fileUploader:
        return (
          <UploadFileFormField
            key={name}
            name={name}
            title={title}
            value={values[name]}
            error={touched[name] ? errors[name] : ''}
            onUpload={(file) => onFieldChange(name, file, sectionIndex)}
            disabled={isDisabled}
            allowedFileTypes={customProps.allowedFileTypes}
            downloadLinks={customProps.downloadLinks || []}
            iconUrl={`${CDN_URL}/icons/upload-field-secondary.svg`}
          />
        );
      case FormFieldType.switch:
        return (
          <SwitchFormField
            key={name}
            name={name}
            title={title}
            value={values[name]}
            onBlur={() => onFieldBlur(name)}
            onChange={(name, val) => onFieldChange(name, val, sectionIndex)}
            disabled={isDisabled}
            required={required}
            information={customProps.tooltipText}
          />
        );
      case FormFieldType.phoneNumber:
        return (
          <EnhancedInternationalPhoneInput
            key={name}
            name={name}
            title={title}
            placeholder={placeholder}
            value={values[name]}
            error={touched[name] ? errors[name] : ''}
            onChange={(v) => onFieldChange(name, v, sectionIndex)}
            onBlur={() => onFieldBlur(name)}
            disabled={isDisabled}
            countriesToShow={customProps.countriesToShow}
            disableDropDown={customProps.disableDropDown}
            editCountryCode={customProps.editCountryCode}
            defaultCountry={customProps.defaultCountry}
            customFormat={customProps.customFormat}
            material={material}
          />
        );
      case FormFieldType.password:
        return (
          <PasswordFormField
            name={name}
            title={inputTitle}
            placeholder={placeholder}
            value={values[name]}
            disabled={isDisabled}
            hideHint={customProps.hideHint}
            strongValidation={customProps.strongValidation}
            onChange={onFieldChangeByEvent}
            error={touched[name] ? errors[name] : ''}
            material={material}
            onBlur={() => {
              onFieldBlur(name);
            }}
          />
        );
      case FormFieldType.checkbox:
        return (
          <CheckboxFormField
            name={name}
            title={inputTitle}
            checked={values[name]}
            onChange={(name, val) => onFieldChange(name, val, sectionIndex)}
            disabled={isDisabled}
          />
        );
      default:
        return <></>;
    }
  };

  return renderInput();
};

export default DynamicFormInput;
